import { useTranslation } from "react-i18next";
import { formatDate, joinValues } from "../../../../utils/helpers";
import { SelectCountry } from "../../../../Component/Form/SelectCountry/SelectCountry";
import { useSearchParams } from "react-router-dom";
import { useLocalizedText } from "../../../../utils/hooks/useLocalizedText";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { DATE_TIME_FORMAT, NUSUK_CITY_ID } from "@/utils/constants";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { PilgrimGroupNameMappingInfiniteSelect } from "@/Component/InfiniteSearchSelect/PilgrimGroupNameMappingInfiniteSelect";
import { useSystem } from "@/utils/hooks/useLocation";
import { SYSTEMS } from "@/utils/permissions";
import { Space } from "antd";
import { Info } from "@/Component/Info";
import { LabelValue } from "@/Component/Value";

export const useArrival = (date, { port: receptionPort } = {}) => {
  // ========
  // hooks
  // ========
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { cachedBasicData: basicData } = useCachedBasicData();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();
  const { currentSystem } = useSystem();
  const isMakkah = currentSystem===SYSTEMS.MAKKAH.code;
  // ==========
  // variables
  // ==========
  const source = searchParams.get("source") || "delegation";

  // =================
  // label values
  // =================
  const nusukLabelValues = [
    {
      key: "capacity",
      label: t("ARRIVAL_CARD_PILGRIMS_COUNT"),
    },
    {
      key: "airline",
      label: t("ARRIVAL_CARD_AIRLINE_COMPANY"),
      render: (_, arrival) =>
        getLocalizedText(arrival?.airline_name_ar, arrival?.airline_name_en) ||
        "-",
    },
    {
      key: "port",
      label: t("UPCOMING_COL_PORT"),
      render: (_, arrival) => getLocalizedText(arrival?.port?.name_ar, arrival?.port?.name_en),
    },
    {
      key: "flight_number",
      label: t("FLIGHTS_MODAL_FLIGHT_NO_LABEL"),
      render: (_, arrival) => {
        return arrival?.flight_number || "-";
      },
    },
    {
      key: "departure_time",
      label: t("FLIGHT_DATE"),
      render: (_, arrival) => {
        // return arrival?.departure_time || "-";
        return arrival?.departure_time
          ? formatDate(
            arrival?.departure_time,
            i18n.language,
            DATE_TIME_FORMAT
          )
          : "-";
      },
    },
    {
      key: "arrival_date",
      label: t("ARRIVAL_DATE"),
      render: (_, arrival) => {
        return arrival?.arrival_date
          ? formatDate(
            arrival?.arrival_date,
            i18n.language,
            DATE_TIME_FORMAT
          )
          : "-";
      },
    },
    {
      key: "contract_id",
      label: t("HOUSING_CONTRACT"),
      render: (value, arrival) => {
        return <Space>
          <Info title={<SelectCachedOptions.Display
            value={value}
            optionKey="contracts"
            campareBy="id"
            render={(option) => <LabelValue.Value value={joinValues([option?.address])} />}
          />}
          />
          {value}
        </Space>
      },
    },
    {
      key: "organizer",
      label: t("ORGANIZER"),
    },
  ];
  const delegationLabelValues = [
    {
      key: "hjaj_number",
      label: t("ARRIVAL_CARD_PILGRIMS_COUNT"),
    },
    {
      key: "flight_number",
      label: t("FLIGHTS_MODAL_FLIGHT_NO_LABEL"),
      render: (_, arrival) => {
        return arrival?.flight_number || "-";
      },
    },
    {
      key: "Airline",
      label: t("ARRIVAL_CARD_AIRLINE_COMPANY"),
      render: (_, arrival) => <SelectCachedOptions.Display
        optionKey="airlines"
        value={arrival?.AirLine}
        campareBy="id"
        render={(option) => [getLocalizedText(option?.name_ar, option?.name_en), `(${option?.code})`].join(" ")} />,
    },
    {
      key: "scheduled_at",
      label: t("ARRIVAL_CARD_FLIGHT_DATE"),
      render: (_, arrival) => {
        return arrival.scheduled_at
          ? formatDate(arrival.scheduled_at, i18n.language, DATE_TIME_FORMAT)
          : "-";
      },
    },
    {
      key: "mission_name",
      label: t("ORGANIZER"),
    },
    {
      key: "company_name",
      label: t("AFFILIATED_ORGANIZER"),
      hide: (arrival) => arrival?.type !== 'group',
    },
    {
      key: "actual_arrival_time",
      label: t("ARRIVAL_CARD_ACTUAL_ARRIVAL_TIME"),
      render: (_, arrival) => {
        const real_datetime = arrival?.real_datetime;
        return !real_datetime
          ? "-"
          : formatDate(real_datetime, i18n.language, DATE_TIME_FORMAT);
      },
    },
    {
      key: "nationality",
      label: t("NATIONALITY"),
      render: (_, arrival) => (
        <SelectCountry.Display value={arrival?.nationality} />
      ),
    },
    {
      key: "number_of_accommodated",
      label: t("ACCOMMODATED_PILGRIMS"),
    },
    {
      key: "entry_port",
      label: t("UPCOMING_COL_PORT"),
      render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
    },
    {
      key: "coming_from_id",
      label: t("ARRIVAL_CARD_ORIGIN"),
      render: (value) => <SelectCachedOptions.Display
        value={value}
        campareBy="id"
        optionKey="directions"
        render={(option) => option?.name}
      />,
    },
    {
      key: "direction_id",
      label: t("RECEPTION_CARD_DIRECTION_LABEL"),
      render: (value) => <SelectCachedOptions.Display
        value={value}
        campareBy="id"
        optionKey="directions"
        render={(option) => option?.name}
      />,
    },
    {
      key: "service_center",
      label: t("ARRIVAL_CARD_SERVICE_CENTER"),
      render: (_, arrival) => (getLocalizedText(arrival?.service_center?.name_ar, arrival?.service_center?.name_en) || '-'),
    },
    {
      key: "contract_id",
      label: t("HOUSING_CONTRACT"),
      render: (value, arrival) => {
        return <Space>
          <Info title={<SelectCachedOptions.Display
            value={value}
            optionKey="contracts"
            campareBy="id"
            render={(option) => <LabelValue.Value value={joinValues([option?.address])} />}
          />}
          />
          {value}
        </Space>
      },
    },
  ];
  /** Rows from `madina/departure-campaigns` (merged campaign + segment), same shape as campaign departure list. */
  const betweenCitiesLabelValues = [
    {
      key: "haj_count",
      label: t("ARRIVAL_CARD_PILGRIMS_COUNT"),
    },
    {
      key: "exit_date",
      label: t("EXIT_DATE"),
      render: (value) => (value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-"),
    },
    {
      key: "means_id",
      label: t("MEANS_OF_TRANSPORT"),
      render: (value) => (
        <SelectCachedOptions.Display
          optionKey="transportation"
          value={value}
          campareBy="id"
          render={(option) => getLocalizedText(option?.name_ar, option?.name_en)}
        />
      ),
    },
    {
      key: "transport_company",
      label: t("TRANSPORT_COMPANY"),
      render: (value, arrival) => (
        <SelectCachedOptions.Display
          optionKey="buses"
          value={value}
          campareBy="company_id"
          render={() =>
            value && (
              <Space>
                {arrival?.transport_company_name_ar || arrival?.transport_company_name_en ? (
                  <Info
                    title={`${t("TRANSPORT_COMPANY")}: ${getLocalizedText(
                      arrival?.transport_company_name_ar,
                      arrival?.transport_company_name_en
                    )} - ${arrival?.transport_contract_no}`}
                  />
                ) : null}
                {getLocalizedText(arrival?.transport_company_name_ar, arrival?.transport_company_name_en)}
              </Space>
            )
          }
        />
      ),
    },
    {
      key: "bus_count",
      label: t("BUSES_COUNT"),
    },
    {
      key: "country_name",
      label: t("ORGANIZER"),
      render: (_, arrival) =>
        getLocalizedText(arrival?.country_name_ar, arrival?.country_name_en) || "-",
    },
    {
      key: isMakkah ? "direction_id" : "location",
      label:
        receptionPort === "JED" ? t("ARRIVAL_DIRECTION") : t("DEPARTURE_DIRECTION"),
      render: (value) => {
        if (receptionPort === "JED" && isMakkah) {
          return t("MADINAH");
        }
        return isMakkah ? (
          <SelectCachedOptions.Display
            optionKey="directions"
            value={value}
            campareBy="id"
            render={(option) => getLocalizedText(option?.name_ar, option?.name_en)}
          />
        ) : (
          value
        );
      },
    },
    {
      key: "port_id",
      label: t("EXIT_PORT"),
      render: (value) => (
        <SelectCachedOptions.Display
          optionKey="ports"
          value={value}
          campareBy="id"
          render={(option) => getLocalizedText(option?.name_ar, option?.name_en) || option?.name}
        />
      ),
    },
    {
      key: "house_madina_contract_id",
      label: t("BUILDING_CONTRACT_NUMBER"),
      render: (value) => (
        <SelectCachedOptions.Display
          optionKey="madinahContracts"
          value={value}
          campareBy="id"
          render={(option) => (
            <Space>
              <Info title={option?.house_commercial_name_ar} />
              {option?.id}
            </Space>
          )}
        />
      ),
    },
    {
      key: "service_center",
      label: t("SERVICE_CENTER"),
      render: (value) => getLocalizedText(value?.name_ar, value?.name_en),
    },
    {
      key: "departure_at",
      label: t("DEPARTURE_AT"),
      render: (value) => (value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-"),
    },
    {
      key: "flight_date",
      label: t("FLIGHT_DATE"),
      render: (value) => (value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-"),
    },
  ];

  const betweenCitiesDepartureFilterFields = [
    { name: "campaign_number", label: t("CAMPAIGN_NUMBER"), type: "text" },
    { name: "exit_date", label: t("EXIT_DATE"), type: "date" },
    {
      name: "means_id",
      label: t("MEANS_OF_TRANSPORT"),
      type: "select",
      optionKey: "transportation",
      options: (transport) => ({
        value: transport.id,
        label: <LabelValue.Value value={[transport.id, transport.name].join(" - ")} />,
      }),
    },
    {
      name: "transport_company",
      label: t("TRANSPORT_COMPANY"),
      type: "select",
      optionKey: "buses",
      options: (bus) => ({
        value: bus.company_id,
        label: <LabelValue.Value value={[bus.company_id, bus.company_name].join(" - ")} />,
      }),
    },
    {
      name: "organizer",
      label: t("ORGANIZER"),
      type: "select",
      optionKey: "missions",
      options: (mission) => ({
        value: mission.id,
        label: <LabelValue.Value value={[mission.id, getLocalizedText(mission.name_ar, mission.name_en)].join(" - ")} />,
      }),
    },
    { name: "departure_at", label: t("DEPARTURE_AT"), type: "date" },
  ];

  const filterFields = [
    {
      name: "date",
      label: t("DATE"),
      type: "date",
    },
    {
      name: "nationality_id",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "campaign_number",
      label: t("CAMPAIGN_NUMBER"),
      type: "input",
    },
    {
      name: "group_number",
      label: t("GROUP_NUMBER"),
      type: "input",
    },
    {
      name: "flight_number",
      label: t("FLIGHT_NUMBER"),
      type: "input",
    },
    {
      name: "airline_id",
      label: t("FLIGHT_AIRLINE"),
      type: "select",
      optionKey: "airlines",
      options: (a) => ({
        value: a.id,
        label: `${getLocalizedText(a?.name_ar, a?.name_en)} (${a.code})`,
      }),
    },
    {
      name: "service_center_id",
      label: t("SERVICE_CENTER"),
      type: "select",
      optionKey: "service_centers",
      options: (sc) => ({
        value: sc.id,
        label: sc.name,
      }),
    },
    {
      name: "entry_port",
      label: t("ENTRY_PORT"),
      type: "select",
      optionKey: "ports",
      options: (ep) => ({
        value: ep.id,
        label: getLocalizedText(ep?.name_ar, ep?.name_en) || ep?.name,
      }),
    },
    {
      name: "direction_id",
      label: t("DIRECTION"),
      type: "select",
      optionKey: "directions",
      options: (d) => ({
        value: d.id,
        label: [d.id, d.name].join(" - "),
      }),
    },
    (watch, setValue) => ({
      name: "mission_id",
      label: t("ORGANIZER"),
      type: "select",
      optionKey: "missions",
      options: (m) => ({
        value: m.id,
        label: getLocalizedText(m.name_ar, m.name_en) || m.name,
      }),
      onChange: () => {
        setValue("group_name_mapping_id", null);
      },
    }),
    (watch, setValue) => ({
      name: "company_id",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      optionKey: "companies",
      options: (c) => ({
        value: c.id,
        label: getLocalizedText(c.name_ar, c.name_en) || c.name,
      }),
      onChange: () => {
        setValue("group_name_mapping_id", null);
      },
    }),
    (watch) => ({
      name: "group_name_mapping_id",
      label: t("MAPPED_GROUP_NAME"),
      type: "infiniteSelect",
      Component: (componentProps) => (
        <PilgrimGroupNameMappingInfiniteSelect
          {...componentProps}
          mission_id={watch("mission_id")}
          company_id={watch("company_id")}
        />
      ),
      placeholder: t("MAPPED_GROUP_NAME"),
    }),
  ];
  const nusukFilterFields = [
    {
      name: "request_number",
      label: t("ARRIVAL_CARD_REQUEST_NUMBER"),
      type: "text",
      placeholder: t("ARRIVAL_CARD_REQUEST_NUMBER"),
    },
    {
      name: "flight_date",
      label: t("FLIGHT_DATE"),
      type: "date",
    },
    {
      name: "arrival_date_from",
      type: "date",
      optionKey: "directions",
      label: t("DATE_FROM"),
    },
    {
      name: "arrival_date_to",
      type: "date",
      optionKey: "directions",
      label: t("DATE_TO"),
    },
    // رقم الرحله
    {
      name: "flight_number",
      type: "text",
      optionKey: "flight_number",
      label: t("FLIGHT_NUMBER"),
    },
    //  كود الرحله
    {
      name: "flight_id",
      type: "text",
      optionKey: "flight_id",
      label: t("FLIGHT_ID"),
    },
    {
      name: "contract_id",
      type: "select",
      optionKey: "contracts",
      label: t("HOUSING_CONTRACT"),
      filter: (c) => String(c.city_id) === String(isMakkah ? NUSUK_CITY_ID.makkah : NUSUK_CITY_ID.madina),
      options: (c) => ({
        value: c.id,
        label: [c.id, c.house_commercial_name_ar].join(" - "),
      }),
    },
    {
      name: "nationality_id",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "airport_code",
      label: t("UPCOMING_COL_PORT"),
      type: "select",
      optionKey: "ports",
      options: (n) => ({
        value: n.code,
        label: [getLocalizedText(n?.name_ar, n?.name_en) || n?.name, n?.code].join(" - "),
      }),
    },
    (watch, setValue) => ({
      name: "mission_id",
      label: t("ORGANIZER"),
      type: "select",
      optionKey: "missions",
      options: (m) => ({
        value: m.id,
        label: getLocalizedText(m.name_ar, m.name_en) || m.name,
      }),
      onChange: () => {
        setValue("group_name_mapping_id", null);
      },
    }),
    (watch, setValue) => ({
      name: "company_id",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      optionKey: "companies",
      options: (c) => ({
        value: c.id,
        label: getLocalizedText(c.name_ar, c.name_en) || c.name,
      }),
      onChange: () => {
        setValue("group_name_mapping_id", null);
      },
    }),
    (watch) => ({
      name: "group_name_mapping_id",
      label: t("MAPPED_GROUP_NAME"),
      type: "infiniteSelect",
      Component: (componentProps) => (
        <PilgrimGroupNameMappingInfiniteSelect
          {...componentProps}
          mission_id={watch("mission_id")}
          company_id={watch("company_id")}
        />
      ),
      placeholder: t("MAPPED_GROUP_NAME"),
    }),
  ];

  const title = {
    group: t("ARRIVAL_CARD_GROUP_NUMBER"),
    campaign: t("ARRIVAL_CARD_CAMPAIGN_NUMBER"),
    nusuk: t("ARRIVAL_CARD_REQUEST_NUMBER"),
    between_cities: t("ARRIVAL_CARD_BETWEEN_CITIES_NUMBER"),
  };

  // const type = isNusuk ? "request_number" : arrivalType;
  // const value = isNusuk ? arrival?.request_number : serial;
  const labelValues = {
    nusuk: nusukLabelValues,
    between_cities: betweenCitiesLabelValues,
    campaign: delegationLabelValues,
    delegation: delegationLabelValues,
  };

  // =================
  // return values
  // =================
  return {
    labelValues: labelValues[source] ?? [],
    filterFields:
      source === "nusuk" ? nusukFilterFields : source === "between_cities" ? betweenCitiesDepartureFilterFields : filterFields,
    title,
    source,
  };
};
